/** Pure date helpers shared by the DateRangePicker and the screens that use it. */

/** A committed date range. Both bounds are local `YYYY-MM-DD` strings. */
export interface DateRange {
  from: string;
  to: string;
}

export function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function startOfMonth(date: Date): Date {
  const copy = new Date(date);
  copy.setDate(1);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function addDays(date: Date, amount: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + amount);
  return copy;
}

export function addMonths(date: Date, amount: number): Date {
  const copy = startOfMonth(date);
  copy.setMonth(copy.getMonth() + amount);
  return copy;
}

export function isSameDay(first: Date, second: Date): boolean {
  return (
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate()
  );
}

export function isSameMonth(first: Date, second: Date): boolean {
  return first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth();
}

/** Local-timezone ISO date (YYYY-MM-DD). Avoids the UTC off-by-one of `toISOString()`. */
export function toIsoDate(date: Date): string {
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Parse a `YYYY-MM-DD` string into a local midnight Date. Falls back to today when blank. */
export function fromIsoDate(value: string | null | undefined): Date {
  if (!value) {
    return startOfDay(new Date());
  }
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) {
    return startOfDay(new Date());
  }
  return new Date(year, month - 1, day);
}

/** Inclusive day span between two dates (same day → 1). */
export function inclusiveDaySpan(from: Date, to: Date): number {
  const millis = startOfDay(to).getTime() - startOfDay(from).getTime();
  return Math.floor(millis / 86_400_000) + 1;
}

/** Inclusive day span for an ISO range, used for period labels/hints. */
export function rangeDayCount(range: DateRange): number {
  return inclusiveDaySpan(fromIsoDate(range.from), fromIsoDate(range.to));
}

export interface CalendarCell {
  date: Date;
  inMonth: boolean;
}

/** 6-week (42-cell) Sunday-first grid for the month containing `monthDate`. */
export function buildMonthGrid(monthDate: Date): CalendarCell[] {
  const first = startOfMonth(monthDate);
  const gridStart = addDays(first, -first.getDay());
  const cells: CalendarCell[] = [];
  for (let index = 0; index < 42; index += 1) {
    const date = addDays(gridStart, index);
    cells.push({ date, inMonth: date.getMonth() === first.getMonth() });
  }
  return cells;
}

/** A range spanning the last `days` days ending today (inclusive). */
export function lastNDaysRange(days: number, today: Date = new Date()): DateRange {
  const end = startOfDay(today);
  return { from: toIsoDate(addDays(end, -(days - 1))), to: toIsoDate(end) };
}
